// ═══════════════════════════════════════════════════════════════════════════════════════════
// PortalRowPreview — the PORTAL'S OWN ROW, rebuilt inside the admin.
//
// WHY THIS FILE EXISTS. The publish drawer is captioned "כך ייראה הפריט באתר", and until now
// that caption was not true: the card there was a flex row with the type chip BESIDE the title,
// no icon tile, no size, no description, and a chevron made of the TEXT character U+2039 — which
// is a Unicode MIRRORING character, so `isRTL ? '‹' : '›'` picked the "right" glyph and the BiDi
// algorithm then drew it reversed, pointing the opposite way from the portal's. The portal draws
// an SVG path, and an SVG path never mirrors. Six differences in a box whose whole job is to be
// the same as something else.
//
// SO THIS IS A PORT, NOT AN IMPRESSION. Every number below was read out of the two shipped
// portal files and is annotated with where it came from:
//   · assets/app.v3.js    — ICONS, MIME_KINDS, EXT_KINDS, fileKind, fmtSize, renderItem's order
//   · assets/style.v5.css — .cat / .item / .ic / .txt / .badge / .desc / .go / .info-body
// If the portal's row changes, THIS FILE IS THE PARITY SURFACE and must change with it (the
// deep-review rule: a fix is not done until everything mirroring it moves too).
//
// THREE PLATFORM TRAPS THIS FILE STEPS AROUND — all measured in this codebase, none theoretical:
//   1. There is NO box-sizing reset anywhere in PulseemReact: every element is content-box. The
//      portal ships `*{box-sizing:border-box}`. A row with 12px of padding inside a fixed track
//      would therefore measure differently HERE than THERE, so every padded box states its own
//      boxSizing.
//   2. jss-rtl flips `makeStyles` sheets that sit under MuiThemeProvider. It does NOT touch
//      inline styles — which is exactly why this file is written in inline styles: the logical
//      properties (borderInlineStart, and the grid areas under direction:rtl) must resolve the
//      way the browser resolves them on the portal, once, with nobody flipping them a second time.
//   3. The portal is `<html lang="he" dir="rtl">`, always — one audience, one language. So this
//      box declares direction:'rtl' instead of inheriting the admin's, otherwise an English-UI
//      editor would be shown a mirror image of the page the agent actually gets.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';
import { eClalItemType } from '../../../Models/ClalCenter/ClalCenter';

/* ── the portal's tokens, as literals (style.v5.css :root) ────────────────────────────────
   Named rather than inlined, so a token change on the portal is a find-and-replace here and
   not a hunt through forty hex strings. */
const CARD = '#FFFFFF';     /* --card   */
const LINE = '#DDD6D2';     /* --line   */
const SKY = '#D2E1FF';      /* --sky    · --clal-sky   תכלת */
const DEEP = '#293768';     /* --deep   · --clal-deep  כחול עמוק */
const INK = '#262B3A';      /* --ink    · --clal-ink   כחול שחור */
const INK2 = '#505B83';     /* --ink-2  derived */
const ACTION = '#3D6CED';   /* --action · --clal-blue  כחול דיגיטלי */
const ROWWASH = '#EDF3FF';  /* --row    first-party Clal wash */
const FONT = '"Assistant", "Segoe UI", Arial, sans-serif';   /* style.v5.css:212 */

/* ── the row's own geometry, mirroring style.v5.css's :root scalars ────────────────────────
   v5 stopped writing the info panel's indent as a literal and started DERIVING it from the four
   numbers the row is built from, because the media query rewrites three of them and a literal
   would go stale on one breakpoint only. The same four are written once here for the same
   reason: the row below and the panel below it must not be able to disagree.
   Desktop values only - this box shows the DESKTOP portal, which is the thing being published. */
const ROW_RAIL = 3;    /* .item border-inline-start - reserved, transparent at rest */
const ROW_PAD_I = 12;  /* .item inline padding */
const ROW_IC = 36;     /* .item .ic, the icon plate */
const ROW_GAP = 12;    /* .item column-gap */
/* Where the title's first glyph sits, from the card's content edge: 3 + 12 + 36 + 12 = 63. */
const ROW_TITLE_INSET = ROW_RAIL + ROW_PAD_I + ROW_IC + ROW_GAP;

/* ── app.v3.js:111-124 · verbatim ──────────────────────────────────────────────────────── */
const ICONS: { [k: string]: string } = {
    link: '<path d="M10 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5"/><path d="M14 4h6v6"/><path d="M20 4l-8.5 8.5"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v5.5"/><path d="M12 7.7h.01"/>',
    doc: '<path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7z"/><path d="M14 3v4h4"/><path d="M9 13h6"/><path d="M9 17h4"/>',
    image: '<rect x="3" y="4.5" width="18" height="15" rx="2"/><circle cx="8.5" cy="10" r="1.6"/><path d="M4 17l4.5-4.5 3.5 3.5 3-2.5L20 17"/>',
    video: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M10.5 9.3l4.5 2.7-4.5 2.7z"/>',
    audio: '<path d="M9 18V6l10-2v12"/><circle cx="6.5" cy="18" r="2.5"/><circle cx="16.5" cy="16" r="2.5"/>',
    file: '<path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7z"/><path d="M14 3v4h4"/>',
    go: '<path d="M14.5 6l-6 6 6 6"/>'
};

interface Kind {
    badge: string;
    ic: string;
}

/* ── app.v3.js:129-153 · verbatim, ORDER INCLUDED (mime wins; extension is the fallback) ── */
const MIME_KINDS: Array<{ re: RegExp; badge: string; ic: string }> = [
    { re: /^application\/pdf$/i, badge: 'PDF', ic: 'doc' },
    { re: /wordprocessingml|^application\/msword$/i, badge: 'Word', ic: 'doc' },
    { re: /spreadsheetml|ms-excel/i, badge: 'Excel', ic: 'doc' },
    { re: /presentationml|ms-powerpoint/i, badge: 'מצגת', ic: 'doc' },
    { re: /^image\//i, badge: 'תמונה', ic: 'image' },
    { re: /^video\//i, badge: 'וידאו', ic: 'video' },
    { re: /^audio\//i, badge: 'שמע', ic: 'audio' }
];

const EXT_KINDS: Array<{ re: RegExp; badge: string; ic: string }> = [
    { re: /\.pdf$/i, badge: 'PDF', ic: 'doc' },
    { re: /\.docx?$/i, badge: 'Word', ic: 'doc' },
    { re: /\.xlsx?$/i, badge: 'Excel', ic: 'doc' },
    { re: /\.pptx?$/i, badge: 'מצגת', ic: 'doc' },
    { re: /\.(png|jpe?g|gif)$/i, badge: 'תמונה', ic: 'image' },
    { re: /\.mp4$/i, badge: 'וידאו', ic: 'video' },
    { re: /\.(mp3|wav)$/i, badge: 'שמע', ic: 'audio' }
];

function fileKind(mime: string | null | undefined, fileName: string | null | undefined): Kind {
    let i: number;
    for (i = 0; i < MIME_KINDS.length; i++) {
        if (mime && MIME_KINDS[i].re.test(mime)) return MIME_KINDS[i];
    }
    for (i = 0; i < EXT_KINDS.length; i++) {
        if (fileName && EXT_KINDS[i].re.test(fileName)) return EXT_KINDS[i];
    }
    return { badge: 'קובץ', ic: 'file' };
}

/** app.v3.js:155-160 · verbatim, INCLUDING the .0 trim — 1.0MB must read "1MB" in both places. */
function fmtSize(n: number | null | undefined): string {
    if (typeof n !== 'number' || !isFinite(n) || n <= 0) return '';
    if (n < 1024) return n + 'B';
    if (n < 1024 * 1024) return Math.round(n / 1024) + 'KB';
    return (n / 1048576).toFixed(1).replace(/\.0$/, '') + 'MB';
}

/** app.v3.js:86-92 · the same svg attributes, so stroke weight, caps and joins match exactly. */
const Glyph = ({ paths, size, style }: { paths: string; size: number; style?: React.CSSProperties }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        width={size}
        height={size}
        style={style}
        dangerouslySetInnerHTML={{ __html: paths }}
    />
);

interface Props {
    itemType: eClalItemType;
    title: string;
    description: string;
    /** FileRef.OriginalName, else the item's stored FileName. Drives the extension fallback. */
    fileName: string;
    /** FileRef.Mime, else the item's stored Mime. Wins over the extension, exactly as on the portal. */
    mime: string | null;
    /** FileRef.SizeBytes, else the item's stored SizeBytes. Absent ⇒ a type-only badge. */
    sizeBytes: number | null;
    infoText: string;
    /** Stand-in for an empty title. The portal would draw a blank line, which tells nobody anything. */
    emptyTitle: string;
}

const PortalRowPreview = ({
    itemType, title, description, fileName, mime, sizeBytes, infoText, emptyTitle
}: Props) => {
    /* renderItem's own branch order — app.v3.js:333-352. */
    const kind: Kind =
        itemType === eClalItemType.INFO ? { badge: 'מידע', ic: 'info' }
            : itemType === eClalItemType.FILE ? fileKind(mime, fileName)
                : { badge: 'קישור', ic: 'link' };

    /* app.v3.js:377-381 — the size JOINS the badge; it is not a line of its own. */
    let badgeText = kind.badge;
    if (itemType === eClalItemType.FILE) {
        const sz = fmtSize(sizeBytes);
        if (sz) badgeText = kind.badge + ' · ' + sz;
    }

    const hasTitle = !!title.trim();
    const shownDesc = (description || '').trim();
    const shownInfo = itemType === eClalItemType.INFO ? (infoText || '').trim() : '';

    return (
        /* .cat — style.v5.css:356-360, plus .cat-body:398 (padding 0 12px 12px) and
           .items:first-child (margin-top 4px). The box-shadow is deliberately omitted: inside a
           drawer it would read as a second elevation over the drawer's own, which is a fact about
           the admin and not about the portal. */
        <div
            style={{
                direction: 'rtl',
                fontFamily: FONT,
                color: INK,
                background: CARD,
                border: '1px solid ' + LINE,
                borderRadius: 12,
                padding: '4px 12px 12px',
                boxSizing: 'border-box'
            }}
        >
            {/* .item — style.v5.css:433-453. The grid IS the design: on the portal .main is
                display:contents so that .badge, a DOM sibling of it, can land between the title
                and the description. Here the children are already flat, so they take their areas
                directly and the result is the same three rows. */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                    gridTemplateAreas: '"ic title go" "ic meta go" "ic desc go"',
                    alignItems: 'start',
                    columnGap: ROW_GAP,
                    rowGap: 0,
                    padding: `10px ${ROW_PAD_I}px`,
                    minHeight: 52,
                    border: 0,
                    borderInlineStart: `${ROW_RAIL}px solid transparent`,
                    borderRadius: 8,
                    background: 'transparent',
                    textAlign: 'start',
                    boxSizing: 'border-box'
                }}
            >
                {/* .ic — the icon plate, --sky fill, --deep glyph (8.67:1). style.v5.css:455-462 */}
                <span
                    style={{
                        gridArea: 'ic',
                        alignSelf: 'center',
                        width: ROW_IC,
                        height: ROW_IC,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                        background: SKY,
                        color: DEEP,
                        boxSizing: 'border-box'
                    }}
                >
                    <Glyph paths={ICONS[kind.ic] || ICONS.file} size={19} />
                </span>

                {/* .txt — style.v5.css:466 */}
                <span
                    style={{
                        gridArea: 'title',
                        display: 'block',
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        overflowWrap: 'anywhere',
                        color: hasTitle ? INK : INK2
                    }}
                >
                    {hasTitle ? title.trim() : emptyTitle}
                </span>

                {/* .badge — style.v5.css:467-475. UNDER the title, not beside it. That is the whole
                    point of the portal's grid, and it is what buys the title its full width. */}
                <span
                    style={{
                        gridArea: 'meta',
                        justifySelf: 'start',
                        marginTop: 3,
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: 0,
                        lineHeight: 1.4,
                        color: INK2,
                        background: CARD,
                        border: '1px solid ' + LINE,
                        borderRadius: 999,
                        padding: '0 9px',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box'
                    }}
                >
                    {badgeText}
                </span>

                {/* .desc — style.v5.css:475-479. The portal hides it with `.desc:empty`; here it
                    simply is not rendered, which is the same result with less to explain. */}
                {!!shownDesc && (
                    <span
                        style={{
                            gridArea: 'desc',
                            display: 'block',
                            marginTop: 3,
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: INK2,
                            maxWidth: '60ch',
                            overflowWrap: 'anywhere'
                        }}
                    >
                        {shownDesc}
                    </span>
                )}

                {/* .go — style.v5.css:498. An SVG PATH, which is the whole fix: U+2039 and U+203A
                    are Bidi-MIRRORING characters and reverse themselves inside an RTL run. A path
                    does not.

                    THE OPEN STATE (R4 finding S-2). The portal has exactly TWO states for an info
                    row, because app.v3.js flips aria-expanded and .hidden in the SAME handler and
                    style.v5.css keys off it:
                      .item.info-tog[aria-expanded="true"] .go{ transform: rotate(90deg); color: var(--action) }
                    This preview draws the panel OPEN (see below), so it has to draw the chevron
                    open too - otherwise it shows a state the site cannot produce: a closed chevron
                    over an expanded panel. Geometry, computed not guessed: the path M14.5 6l-6 6 6 6
                    has apex vector [-3.5, 0], i.e. pointing to the inline start; rotate(90deg)
                    takes it to [0, -3.5], pointing up - which is what the site draws. */}
                <Glyph
                    paths={ICONS.go}
                    size={18}
                    style={{
                        gridArea: 'go',
                        alignSelf: 'center',
                        color: shownInfo ? ACTION : INK2,
                        transform: shownInfo ? 'rotate(90deg)' : undefined
                    }}
                />
            </div>

            {/* .info-body — style.v5.css, drawn OPEN because a preview that hides the content is
                not previewing it. On the site this takes one click on the row.

                v5 MOVED THIS AND SO DOES THE MIRROR. The panel used to begin at 12px, level with
                the icon plate; it now begins at ROW_TITLE_INSET, level with the TITLE, because the
                plate is a marker and not the start of the text stream. What lands on the title is
                the panel's EDGE - its text starts 19px further in (3px rail + 16px padding), which
                is the ordinary callout convention. The measure cap went 60ch -> 100ch at the same
                time. Both were decided 08.09.2026 by the owner and a five-lens UI/UX panel; the
                reasoning lives in style.v5.css beside the rule itself.

                The cap is effectively inert HERE and that is expected, not a bug: the drawer is
                min(780px, 100%), so the container decides the width long before 100ch (788px)
                does. It is written anyway, because this file's job is to say what the portal says. */}
            {!!shownInfo && (
                <div
                    style={{
                        marginBlockStart: 4,                  /* + the row's 10px padding = 14 above */
                        marginBlockEnd: 12,                   /* + the card's 12px padding = 24 below */
                        marginInlineStart: ROW_TITLE_INSET,   /* 63 - DERIVED, never typed */
                        marginInlineEnd: ROW_PAD_I,           /* 12 - the row's own inline padding */
                        padding: '14px 16px',
                        background: ROWWASH,
                        border: '1px solid ' + SKY,
                        borderInlineStart: '3px solid ' + ACTION,
                        borderRadius: 8,
                        color: INK,
                        fontSize: 16,
                        lineHeight: 1.7,
                        maxWidth: '100ch',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        boxSizing: 'border-box'
                    }}
                >
                    {shownInfo}
                </div>
            )}
        </div>
    );
};

export default PortalRowPreview;
